import React from 'react';
import { Link } from 'react-router-dom';
import { trackFreeTrial, trackDemoRequest } from '../utils/tracking';

/**
 * /pcrecruiter-sourcing
 * Dedicated landing page targeting PCRecruiter ecosystem keywords.
 * SEO targets: pcrecruiter sourcing, pcr ats integration, pcrecruiter tools,
 *              pcrecruiter extension, source candidates pcrecruiter
 */
const PCRecruiterSourcing = () => {
  const handleTrial = (loc) => trackFreeTrial(loc);
  const handleDemo = (loc) => trackDemoRequest(loc);

  return (
    <>
      {/* Hero */}
      <section className="hero">
        <div className="container hero-content fade-up" style={{ maxWidth: '780px' }}>
          <div className="section-badge blue">PCRecruiter + kNine</div>
          <h1 className="hero-title" style={{ fontSize: '2.5rem', lineHeight: 1.15 }}>
            AI Candidate Sourcing for <span className="text-gradient">PCRecruiter</span>
          </h1>
          <p className="hero-subtitle" style={{ fontSize: '1.1rem', maxWidth: '640px', margin: '0 auto 28px' }}>
            kNine works alongside PCRecruiter to help recruiters source net-new
            candidates and companies outside their existing ATS database.
          </p>

          <div className="hero-actions" style={{ marginBottom: '32px' }}>
            <a
              href="https://app.knine.ai/register"
              className="btn btn-gradient btn-lg"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleTrial('pcr_hero')}
            >
              Start Free Trial — No Credit Card
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </div>

          <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)', textAlign: 'center' }}>
            PCRecruiter manages your recruiting workflow.
            <strong style={{ color: 'var(--primary)' }}> kNine finds the candidates your ATS doesn't already know about.</strong>
          </p>
        </div>
      </section>

      {/* The Problem */}
      <section style={{ paddingTop: '48px', paddingBottom: '48px' }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          <h2 className="section-title text-center" style={{ fontSize: '1.6rem', marginBottom: '32px' }}>
            The Challenge PCRecruiter Users Face
          </h2>
          <div className="feature-grid" style={{ maxWidth: '800px', margin: '0 auto' }}>
            {[
              {
                icon: '🔍',
                title: 'Find Candidates Not in Your ATS',
                desc: 'Your PCRecruiter database is valuable, but it only contains people you\'ve already found. kNine opens up 100M+ net-new contacts to source from.',
              },
              {
                icon: '📧',
                title: 'Get Verified Emails & Phone Numbers',
                desc: 'Stop guessing at contact info. kNine provides 95%+ accurate emails and direct phone numbers — verified in real-time before you reach out.',
              },
              {
                icon: '🏢',
                title: 'Identify Hiring Managers & Decision Makers',
                desc: 'Source not just candidates, but the hiring managers and decision makers you need for BD. Cooper finds company contacts while Roxi finds candidates.',
              },
            ].map((item) => (
              <div key={item.title} className="card" style={{ padding: '28px' }}>
                <div style={{ fontSize: '2rem', marginBottom: '12px' }}>{item.icon}</div>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '8px' }}>
                  {item.title}
                </h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7, margin: 0 }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The Solution */}
      <section className="bg-off-white" style={{ paddingTop: '48px', paddingBottom: '48px' }}>
        <div className="container" style={{ maxWidth: '800px' }}>
          <h2 className="section-title text-center" style={{ fontSize: '1.6rem', marginBottom: '12px' }}>
            How kNine Works with <span className="text-gradient">PCRecruiter</span>
          </h2>
          <p className="section-subtitle text-center mx-auto" style={{ marginBottom: '36px' }}>
            kNine adds AI-powered sourcing on top of your existing PCRecruiter workflow.
            No data migration. No workflow changes. Just more candidates.
          </p>

          <div className="feature-grid-2" style={{ maxWidth: '800px', margin: '0 auto' }}>
            {[
              {
                icon: '🤖',
                title: 'AI-Powered Candidate Search',
                desc: 'Describe your ideal candidate in plain English. Roxi understands context, skills adjacencies, and implicit requirements — no boolean strings needed.',
              },
              {
                icon: '📊',
                title: '100M+ Verified Contacts',
                desc: 'Access a database of over 100 million contacts with 95%+ email accuracy and real-time verification. Find candidates your ATS has never seen.',
              },
              {
                icon: '🔗',
                title: 'Seamless PCRecruiter Integration',
                desc: 'Push sourced contacts directly into PCRecruiter candidate records. Field mapping and data sync are pre-configured by our team — zero setup on your end.',
              },
              {
                icon: '🏢',
                title: 'Sourcing for Recruiting & Sales',
                desc: 'Roxi sources candidates. Cooper sources companies and hiring managers. Both work alongside PCRecruiter to power your full pipeline.',
              },
            ].map((item) => (
              <div key={item.title} className="card" style={{ padding: '28px' }}>
                <div style={{ fontSize: '1.75rem', marginBottom: '12px' }}>{item.icon}</div>
                <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '8px' }}>
                  {item.title}
                </h3>
                <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7, margin: 0 }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works flow */}
      <section style={{ paddingTop: '48px', paddingBottom: '48px' }}>
        <div className="container" style={{ maxWidth: '700px' }}>
          <h2 className="section-title text-center" style={{ fontSize: '1.5rem', marginBottom: '36px' }}>
            Up and Running in Minutes
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            {[
              { step: '1', title: 'Connect your PCRecruiter account', desc: 'Authorize kNine through a secure API connection. Takes under a minute.' },
              { step: '2', title: 'Search with AI', desc: 'Use Roxi to find candidates or Cooper to find companies. Describe what you need in plain English.' },
              { step: '3', title: 'Push to PCRecruiter', desc: 'One click sends sourced contacts — with verified emails, phone numbers, and enrichment data — directly into your ATS.' },
            ].map((item) => (
              <div key={item.step} className="card-flat" style={{ display: 'flex', gap: '20px', alignItems: 'flex-start', padding: '24px 28px' }}>
                <div style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: 'var(--primary)',
                  color: 'white',
                  fontSize: '1rem',
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {item.step}
                </div>
                <div>
                  <h4 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--text-dark)', marginBottom: '4px' }}>
                    {item.title}
                  </h4>
                  <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', lineHeight: 1.7, margin: 0 }}>
                    {item.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mid CTA */}
      <section className="bg-off-white" style={{ paddingTop: '40px', paddingBottom: '40px' }}>
        <div className="container" style={{ maxWidth: '680px', textAlign: 'center' }}>
          <h2 className="section-title" style={{ fontSize: '1.5rem', marginBottom: '12px' }}>
            Ready to Source Beyond Your ATS?
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.95rem', lineHeight: 1.7, marginBottom: '24px' }}>
            14-day free trial. No credit card. Works alongside your existing PCRecruiter setup.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <a
              href="https://app.knine.ai/register"
              className="btn btn-gradient btn-lg"
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleTrial('pcr_mid')}
            >
              Start Free Trial
            </a>
            <Link to="/contact" className="btn btn-outline btn-lg" onClick={() => handleDemo('pcr_mid')}>
              Schedule a Demo
            </Link>
          </div>
        </div>
      </section>

      {/* Why kNine + PCR */}
      <section style={{ paddingTop: '48px', paddingBottom: '48px' }}>
        <div className="container" style={{ maxWidth: '720px' }}>
          <h2 className="section-title text-center" style={{ fontSize: '1.5rem', marginBottom: '28px' }}>
            Why PCRecruiter Users Choose kNine
          </h2>
          <div className="comparison-table-wrapper">
            <table className="comparison-table">
              <thead>
                <tr>
                  <th style={{ minWidth: '220px' }}>Capability</th>
                  <th>PCRecruiter Alone</th>
                  <th className="highlight">PCRecruiter + kNine</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { f: 'Source candidates in your ATS', pcr: true, both: true },
                  { f: 'Source net-new candidates (100M+)', pcr: false, both: true },
                  { f: 'AI natural language search', pcr: false, both: true },
                  { f: 'Verified emails & phone numbers', pcr: false, both: true },
                  { f: 'Identify hiring managers for BD', pcr: false, both: true },
                  { f: 'Real-time data verification', pcr: false, both: true },
                  { f: 'Chrome extension sourcing', pcr: false, both: true },
                  { f: 'Analytics & reporting', pcr: true, both: true },
                ].map((row) => (
                  <tr key={row.f}>
                    <td className="feature-name">{row.f}</td>
                    <td>{row.pcr ? <span className="check-icon">✓</span> : <span className="cross-icon">✗</span>}</td>
                    <td style={{ background: '#f0f7ff' }}><strong><span className="check-icon">✓</span></strong></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section>
        <div className="container">
          <div className="cta-section">
            <div className="cta-paw" style={{ top: '-20px', left: '-10px', transform: 'rotate(-25deg)' }}>🐾</div>
            <div className="cta-paw" style={{ bottom: '-20px', right: '-10px', transform: 'rotate(15deg)' }}>🐾</div>
            <h2>Source Candidates Your ATS Doesn't Know About</h2>
            <p>
              PCRecruiter manages your workflow. kNine finds the candidates. Try free today.
            </p>
            <a
              href="https://app.knine.ai/register"
              className="btn btn-white btn-lg"
              style={{ border: 'none' }}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => handleTrial('pcr_bottom')}
            >
              Start Free Trial Now
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default PCRecruiterSourcing;
